import React from 'react';
import { RoutineTask } from '../../../types/RoutineTask';
import { TaskStage } from '../../../types/Task';
import { RowActions } from '@/components/ui';

interface RoutineTaskActionsProps {
  task: RoutineTask;
  onAction: (action: string) => void;
  canReopenTask?: boolean;
  canCloneTask?: boolean;
  /**
   * Optional row-expander chevron, rendered ahead of the kebab. RoutineWorkTable used to render
   * its own chevron beside this component from two different call sites (desktop row + mobile
   * card), each its own <Button>, so the two never lined up - the "action dots are misaligned
   * when one task has expandable items" report. Threading it through here makes the expander and
   * the kebab one RowActions control group at one fixed width in one place. Shape matches
   * RowActions' own (unexported) RowExpander prop.
   */
  expander?: {
    expanded: boolean;
    onToggle: () => void;
    title?: string;
  };
}

// Previously a raw <button> plus a hand-rolled fixed-position menu with its own viewport
// flip/clamp math and a full-screen click-catcher, with no keyboard or ARIA support.
// RowActions inherits all of that from the shared Dropdown. Gating is unchanged.
const RoutineTaskActions: React.FC<RoutineTaskActionsProps> = ({
  task,
  onAction,
  canReopenTask = true,
  canCloneTask = false,
  expander,
}) => (
  <RowActions
    align="left"
    className="tw-justify-center"
    expander={expander}
    actions={[
      { kind: 'edit', label: 'Edit Task', onClick: () => onAction('edit') },
      {
        kind: 'custom',
        icon: 'bi-copy',
        label: 'Clone Task',
        onClick: () => onAction('clone-task'),
        hidden: !canCloneTask || task.status === TaskStage.CANCELLED,
      },
      {
        kind: 'custom',
        icon: 'bi-file-earmark-pdf',
        label: 'Print PDF',
        onClick: () => onAction('print-pdf'),
      },
      { kind: 'history', label: 'Task History', onClick: () => onAction('history') },
      {
        kind: 'custom',
        icon: 'bi-check-circle',
        label: 'Close Task',
        onClick: () => onAction('close'),
        hidden: task.status !== TaskStage.ACTIVE,
      },
      {
        kind: 'custom',
        icon: 'bi-arrow-counterclockwise',
        label: 'Reopen Task',
        onClick: () => onAction('reopen'),
        hidden: !(task.status === TaskStage.CLOSED && canReopenTask),
      },
      {
        // Same handler as Reopen — cancelled tasks get the "Reactivate" wording.
        kind: 'custom',
        icon: 'bi-arrow-counterclockwise',
        label: 'Reactivate Task',
        onClick: () => onAction('reopen'),
        hidden: !(task.status === TaskStage.CANCELLED && canReopenTask),
      },
      { kind: 'cancel', label: 'Cancel Task', onClick: () => onAction('cancel') },
    ]}
  />
);

export default RoutineTaskActions;
